import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Autocomplete, Box, Chip, CircularProgress, IconButton, TextField, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CloseOutlined } from '@mui/icons-material';
import fileUpload from '../../proposals/new-proposal-dilaog/file-upload.svg';
import { createKnowledgeBaseAsync, getKnowledgeBasesAsync } from '../../../../application/features/knowledgeBase/knowledgeBaseThunk';
import { useAppDispatch, useAppSelector } from '../../../../application/store/useStore';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react';
import { useCompanyContext } from '../../../../application/context/CompanyContext';
import { getAllExpertisesAsync } from '../../../../application/features/expertise/expertiseThunk';
export const NewKnowledgeBaseDialog = ({ accept = '.pdf', onClose, openDialog }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [isUploading, setIsUploading] = React.useState(false);
    const [files, setFiles] = React.useState([]);
    const [isDragging, setIsDragging] = React.useState(false);
    const [title, setTitle] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [selectedExpertises, setSelectedExpertises] = React.useState([]);
    const [selectedSubExpertises, setSelectedSubExpertises] = React.useState([]);
    const dispatch = useAppDispatch();
    const { selectedCompanyId } = useCompanyContext();
    const { expertises } = useAppSelector((state) => state.expertise);
    React.useEffect(() => {
        if (openDialog && expertises.length === 0) {
            dispatch(getAllExpertisesAsync());
        }
    }, [openDialog, dispatch, expertises.length]);
    React.useEffect(() => {
        if (!openDialog) {
            setFiles([]);
            setTitle('');
            setDescription('');
            setSelectedExpertises([]);
            setSelectedSubExpertises([]);
        }
    }, [openDialog]);
    const removeFile = (index) => {
        setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };
    const handleFileChange = (event) => {
        const fileList = event.target.files;
        if (fileList) {
            const selectedFiles = Array.from(fileList);
            const validFiles = [];
            selectedFiles.forEach((file) => {
                if (file.type === 'application/pdf') {
                    validFiles.push(file);
                }
            });
            if (validFiles.length > 0) {
                setFiles((prevFiles) => [...prevFiles, ...validFiles]);
            }
        }
    };
    const handleDragOver = (event) => {
        event.preventDefault();
        setIsDragging(true);
    };
    const handleDragLeave = () => {
        setIsDragging(false);
    };
    const handleDrop = (event) => {
        event.preventDefault();
        setIsDragging(false);
        const droppedFiles = Array.from(event.dataTransfer.files);
        const validFiles = droppedFiles.filter((file) => file.type === 'application/pdf');
        if (validFiles.length > 0) {
            setFiles((prevFiles) => [...prevFiles, ...validFiles]);
        }
    };
    const onSubmit = async () => {
        if (!selectedCompanyId || selectedCompanyId === 'ALL') {
            toast.error(t('companyRequired'));
            return;
        }
        if (!title.trim()) {
            toast.error(t('titleRequired') || 'Please enter title');
            return;
        }
        if (selectedExpertises.length === 0) {
            toast.error(t('expertiseRequired') || 'Please select at least one expertise');
            return;
        }
        if (files.length === 0) {
            toast.error(t('Please upload at least one file'));
            return;
        }
        setIsUploading(true);
        // Build expertiseIdList from selected expertises and sub-expertises
        const expertiseIdList = selectedExpertises.map((exp) => {
            // Get sub-expertise IDs that belong to this expertise
            const subExpertiseIds = selectedSubExpertises
                .filter((subExp) => exp.subExpertise?.some((se) => se.id === subExp.id))
                .map((subExp) => subExp.id);
            return {
                expertiseId: exp.id,
                subExpertiseIds
            };
        });
        const formData = {
            companyId: selectedCompanyId,
            title: title.trim(),
            description: description.trim() || undefined,
            expertiseIdList,
            files
        };
        dispatch(createKnowledgeBaseAsync(formData))
            .unwrap()
            .then((response) => {
            toast.success(t('knowledgeBaseCreationSuccess'));
            dispatch(getKnowledgeBasesAsync(selectedCompanyId));
            onClose();
            setFiles([]);
            setTitle('');
            setDescription('');
            setSelectedExpertises([]);
            setSelectedSubExpertises([]);
        })
            .catch((error) => {
            toast.error(t('knowledgeBaseCreationFail'));
        })
            .finally(() => {
            setIsUploading(false);
        });
    };
    // Get all available sub-expertises from selected expertises
    const availableSubExpertises = React.useMemo(() => {
        const subExps = [];
        selectedExpertises.forEach((exp) => {
            if (exp.subExpertise && exp.subExpertise.length > 0) {
                exp.subExpertise.forEach((se) => {
                    subExps.push({ ...se, parentExpertiseName: exp.name });
                });
            }
        });
        return subExps;
    }, [selectedExpertises]);
    return (_jsxs(Dialog, { maxWidth: 'md', fullWidth: true, sx: {
            '& .MuiDialog-paper': {
                overflow: 'hidden',
                backgroundColor: theme.palette.background.paper
            }
        }, open: openDialog, onClose: onClose, children: [_jsxs(DialogTitle, { sx: {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    borderBottom: `1px solid ${theme.palette.divider}`
                }, children: [_jsx(Typography, { variant: 'h6', children: t('addNewDocument') || 'Add New Document' }), _jsx(IconButton, { "aria-label": 'close', onClick: onClose, sx: {
                            color: theme.palette.text.primary
                        }, children: _jsx(CloseOutlined, {}) })] }), _jsx(DialogContent, { sx: {
                    paddingTop: '24px !important',
                    backgroundColor: theme.palette.background.paper
                }, children: _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 3, children: [_jsxs(Box, { children: [_jsx(Typography, { variant: 'subtitle2', color: 'primary', mb: 1.5, children: t('documentDetails') || 'Document Details' }), _jsx(TextField, { fullWidth: true, label: t('title') || 'Title', value: title, onChange: (e) => setTitle(e.target.value), required: true, sx: { mb: 2 } }), _jsx(TextField, { fullWidth: true, label: t('description') || 'Description', value: description, onChange: (e) => setDescription(e.target.value), multiline: true, rows: 3, sx: { mb: 2 } }), _jsx(Autocomplete, { multiple: true, value: selectedExpertises, options: expertises, getOptionLabel: (option) => option.name || '', onChange: (_, value) => {
                                        setSelectedExpertises(value);
                                        // Filter out sub-expertises that don't belong to newly selected expertises
                                        setSelectedSubExpertises((prev) => prev.filter((subExp) => value.some((exp) => exp.subExpertise?.some((se) => se.id === subExp.id))));
                                    }, renderInput: (params) => (_jsx(TextField, { ...params, label: t('category') || 'Category', placeholder: selectedExpertises.length === 0
                                            ? t('selectCategory') || 'Select categories'
                                            : '', required: true })), renderTags: (value, getTagProps) => value.map((option, index) => (_createElement(Chip, { label: option.name, ...getTagProps({ index }), key: option.id, size: 'small' }))), sx: { mb: 2 } }), _jsx(Autocomplete, { multiple: true, value: selectedSubExpertises, options: availableSubExpertises, groupBy: (option) => option.parentExpertiseName, getOptionLabel: (option) => option.name || '', onChange: (_, value) => setSelectedSubExpertises(value), disabled: selectedExpertises.length === 0, renderInput: (params) => (_jsx(TextField, { ...params, label: t('subCategory') || 'Subcategory', placeholder: selectedSubExpertises.length === 0
                                            ? t('selectSubcategory') || 'Select subcategories'
                                            : '' })), renderTags: (value, getTagProps) => value.map((option, index) => (_createElement(Chip, { label: option.name, ...getTagProps({ index }), key: option.id, size: 'small' }))) })] }), _jsxs(Box, { children: [_jsx(Typography, { variant: 'subtitle2', color: 'primary', mb: 1.5, children: t('files') || 'Files' }), _jsxs(Box, { onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, sx: {
                                        border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
                                        borderRadius: 2,
                                        padding: 4,
                                        textAlign: 'center',
                                        backgroundColor: isDragging ? theme.palette.action.hover : 'transparent',
                                        cursor: 'pointer'
                                    }, children: [_jsx("input", { type: 'file', accept: accept, multiple: true, onChange: handleFileChange, style: { display: 'none' }, id: 'file-upload' }), _jsx("label", { htmlFor: 'file-upload', children: _jsxs(Box, { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, children: [_jsx("img", { src: fileUpload, alt: 'Upload', width: 60 }), _jsxs(Typography, { variant: 'body1', color: 'text.secondary', children: [t('drag&Drop'), " ", t('or'), " ", t('browse')] }), _jsx(Typography, { variant: 'caption', color: 'text.secondary', children: t('pdfFilesOnly') || 'PDF files only' })] }) })] }), files.length > 0 && (_jsx(Box, { mt: 2, display: 'flex', flexDirection: 'column', gap: 1, children: files.map((file, index) => (_jsxs(Box, { sx: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: 2,
                                            backgroundColor: theme.palette.background.default,
                                            borderRadius: 1
                                        }, children: [_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1, children: [_jsx(Icon, { icon: 'material-icon-theme:pdf', width: 24, height: 24 }), _jsx(Typography, { variant: 'body2', children: file.name })] }), _jsx(IconButton, { size: 'small', onClick: () => removeFile(index), children: _jsx(CloseOutlined, { fontSize: 'small' }) })] }, index))) }))] })] }) }), _jsxs(DialogActions, { sx: {
                    padding: 2,
                    backgroundColor: theme.palette.background.paper,
                    borderTop: `1px solid ${theme.palette.divider}`
                }, children: [_jsx(Button, { onClick: onClose, variant: 'outlined', children: t('cancel') }), _jsx(Button, { onClick: onSubmit, variant: 'contained', disabled: isUploading, startIcon: isUploading ? _jsx(CircularProgress, { size: 16 }) : null, children: isUploading ? t('uploading') : t('uploadDocument') || 'Upload Document' })] })] }));
};
export default NewKnowledgeBaseDialog;
